import { useState, useEffect } from 'react';
import { Truck, Banknote, Square, Pencil, Receipt, CreditCard, Hourglass, Undo2 } from 'lucide-react';
import StalyPruh from './StalyPruh';
import ShiftCardCta from './ShiftCardCta';
import NastaveniNavigace from '../RozvozCockpit/NastaveniNavigace';

const fmtTime = t => (t || '').toString().slice(0, 5);
const fmtKc = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

/**
 * Kolik zbývá do konce směny. Odpracovaný čas tu byl dřív, ale mezi
 * „Start 16:30" a „Konec 23:00" dává odpočet lepší odpověď na otázku, kvůli
 * které se člověk na osu dívá: kde na té cestě zrovna jsem.
 *
 * Tiká po 15 s, ne po sekundách: minuty se mění jednou za minutu a vteřinový
 * interval by jen budil telefon.
 */
function useZbyva(smena, endTimeOverride) {
    const [label, setLabel] = useState('');
    const konecStr = endTimeOverride || (smena?.cas_do || '').slice(0, 5);
    const datum = typeof smena?.datum === 'string' ? smena.datum.slice(0, 10) : smena?.datum;
    useEffect(() => {
        if (!datum || !konecStr) { setLabel(''); return; }
        const konec = new Date(`${datum}T${konecStr}:00`);
        const tick = () => {
            const sec = Math.floor((konec - Date.now()) / 1000);
            // Po plánovaném konci odpočet nemá co odpočítávat — ukáže, o kolik
            // se přetáhlo. Je to přesně ten údaj, kvůli kterému se v tu chvíli
            // na osu kouká.
            if (sec <= 0) {
                const pres = Math.floor(-sec / 60);
                setLabel(pres >= 60 ? `přesčas ${Math.floor(pres / 60)}h ${(pres % 60).toString().padStart(2, '0')}m` : `přesčas ${pres}m`);
                return;
            }
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            setLabel(h > 0 ? `zbývá ${h}h ${m.toString().padStart(2, '0')}m` : `zbývá ${m}m`);
        };
        tick();
        const iv = setInterval(tick, 15000);
        return () => clearInterval(iv);
    }, [datum, konecStr]);
    return label;
}

/**
 * Kolik směny uběhlo (0–1) a kolik minut zbývá do konce — pro čáru času
 * a pro vrácení „Ukončit směnu" do panelu na konci. Tiká po 30 s.
 */
function usePrubehSmeny(smena, endTimeOverride) {
    const [stav, setStav] = useState({ pomer: null, minutDoKonce: null });
    const odStr = (smena?.cas_od || '').slice(0, 5);
    const doStr = endTimeOverride || (smena?.cas_do || '').slice(0, 5);
    const datum = typeof smena?.datum === 'string' ? smena.datum.slice(0, 10) : smena?.datum;
    useEffect(() => {
        if (!datum || !odStr || !doStr) { setStav({ pomer: null, minutDoKonce: null }); return undefined; }
        const od = new Date(`${datum}T${odStr}:00`).getTime();
        let konec = new Date(`${datum}T${doStr}:00`).getTime();
        if (konec <= od) konec += 24 * 3600 * 1000;   // směna přes půlnoc
        const tick = () => {
            const ted = Date.now();
            setStav({ pomer: (ted - od) / (konec - od), minutDoKonce: Math.floor((konec - ted) / 60000) });
        };
        tick();
        const iv = setInterval(tick, 30000);
        return () => clearInterval(iv);
    }, [datum, odStr, doStr]);
    return stav;
}

/**
 * Karta probíhající směny — sjednocená hlavička (fáze nese barva karty),
 * osa fází s odpočtem, dlaždice AUTO + POKLADNA + KOLEGOVÉ a CTA ve spodní
 * hraně pruhu.
 */
export default function ShiftCardActive({
    smena, dochazka, tym = [],
    pokladnaSmena,
    phase = 'prubeh',                    // 'prubeh' | 'ukonceni'
    canCta = true,
    checklistDone = true,                // povinné položky checklistu aktuální fáze splněny
    chybiKontrol = 0,                    // kolik jich zbývá — pro jméno překážky na tlačítku
    endTimeOverride = null,
    onCtaEditTime,
    onAuto,                              // klepnutí na Auto — přepne plochu „Já za volantem"
    onPrevzitAuto,                       // bez auta za běhu směny — výběr auta k převzetí
    onPokladna,                          // klepnutí na Pokladnu — přepne plochu „Moje pokladna"
    plocha = null,                       // která plocha je otevřená ('ridic' | 'pokladna' | null)
    onCta,                               // active: ukončit směnu; end: final clock-out
    onUzaverka,                          // end pre-uzaverka: vyúčtování pokladny (rozvoz)
    onKorekce,                           // end: korekce hlavní pokladny (manažer)
    onVratitAuto,
    onVratitDoPrubehu,                   // end: ukončení omylem → zpět do průběhu
    onOpenTeam,
    cekaNaVyrovnani = false,             // bez pokladny: čeká na předání zálohy
    onOtevritFazi,                       // klik do osy fází → PhaseModal
    aktivniFazeId = null,
    busy, error,
    isDark, isMobile = false,
    children,                            // pracovní plocha pod pruhem
    /* Uzávěrka běží v pracovní ploše. Mění dvě věci: schová tlačítko v hraně
       pruhu (uzávěrka si nese svoje, jinak by tu byla dvě) a přepíše
       dlaždici pokladny. */
    uzaverkaBezi = false,
}) {
    // Zamčené tlačítko nese jméno překážky, ne akci, kterou nejde provést.
    // Stejný vzor jako „Čeká na potvrzení manažera" — našedlé tlačítko bez
    // vysvětlení se čte jako rozbitá aplikace, ne jako „ještě něco chybí".
    const zbyvaLabel = chybiKontrol === 1 ? 'Zbývá 1 kontrola'
        : chybiKontrol < 5 ? `Zbývají ${chybiKontrol} kontroly`
        : `Zbývá ${chybiKontrol} kontrol`;

    const [navigaceOtevrena, setNavigaceOtevrena] = useState(false);
    const isEnd = phase === 'ukonceni';
    const vozidlo = smena?.vozidlo;
    const maAuto = !!vozidlo && !smena?.vozidlo_vraceno_at;
    const maPokladnu = !!pokladnaSmena;

    // Hlavní pokladna je trvalý singleton — nevyúčtovává se jako rozvozový flek.
    // Místo toho manažer na konci směny provede korekci pokladny (rychlou korekci
    // karty nebo kompletní uzávěrku); příznak hotovo = dochazka.pokladna_korekce_at.
    const jeHlavniPokladna = pokladnaSmena?.typ === 'bistro_hlavni';
    const korekceHotova = !!dochazka?.pokladna_korekce_at;
    const pokladnaUzavrena = jeHlavniPokladna
        ? korekceHotova
        : !!(pokladnaSmena?.stav && pokladnaSmena.stav !== 'otevreno');
    const ukonceniNeedsVratitAuto = isEnd && maAuto;
    const ukonceniNeedsUzaverka = isEnd && !maAuto && maPokladnu && !jeHlavniPokladna && !pokladnaUzavrena;
    const ukonceniNeedsKorekce = isEnd && !maAuto && maPokladnu && jeHlavniPokladna && !korekceHotova;
    const ukonceniLocked = isEnd && !maAuto && pokladnaUzavrena;

    // Odeslané, ale nepotvrzené vyúčtování. Pro rozvozce to není hotovo:
    // server ho bez potvrzení manažera neodhlásí. Bez vlastního stavu tu
    // svítilo „Odhlásit v X" našedlo a bez vysvětlení — vypadalo to jako
    // rozbitá aplikace, ne jako čekání na člověka.
    const cekaNaManazera = isEnd && !jeHlavniPokladna
        && (cekaNaVyrovnani || pokladnaSmena?.stav === 'ceka_na_schvaleni');

    // Konec směny rozvozce je od odeslání uzávěrky pevný: manažer z něj
    // spočítal mzdu a předali si podle něj peníze. Tlačítko ukáže ten čas
    // (i po obnovení stránky, kdy volba z uzávěrky v paměti není) a „Jiný
    // čas" už nenabízí — přepsal by peníze, které jsou venku.
    const konecPevny = isEnd && !jeHlavniPokladna && pokladnaUzavrena;
    const odeslanyKonec = (() => {
        const iso = dochazka?.navrzeny_clock_out;
        if (!iso) return null;
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? null
            : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    })();

    // Barva akce: zelená v průběhu i ukončení (fialová se podle normy na
    // ploše nepoužívá). Fázi rozliší osa a horní řádek panelu.
    const phaseColor = '#10B981';

    // CTA kaskáda kroků — barvu mají všechny stejnou (barva fáze), krok
    // rozliší ikona a text:
    //   prubeh                       → "Ukončit směnu"
    //   ukonceni + má auto           → "Uvolnit vozidlo"
    //   ukonceni + uzaverka potřeba  → "Uzavřít pokladnu" – rozvoz
    //   ukonceni + korekce potřeba   → "Uzavřít pokladnu" – hlavní pokladna
    //   ukonceni + vše hotové        → "Odhlásit v X"
    let ctaLabel, ctaIcon, ctaOnClick, ctaCanClick;
    if (ukonceniNeedsVratitAuto) {
        ctaLabel = 'Uvolnit vozidlo';
        ctaIcon = <Truck size={16} />;
        ctaOnClick = onVratitAuto;
        // Bez ohledu na checklist. Uvolnění auta je jediný krok ukončení,
        // který nedrží jen tenhle člověk: dokud vůz visí na něm, nemůže ho
        // převzít nikdo další. Vázat ho na odklikané položky znamená nechat
        // auto stát kvůli papírování, které se stihne potom — a přesně proto
        // je ten krok samostatný, ne součástí odhlášení. Server ho taky
        // nepodmiňuje (vratitAuto nic o checklistu neví).
        ctaCanClick = true;
    } else if (ukonceniNeedsUzaverka) {
        // Uzávěrka rozvozce. Tlačítko pojmenovává **krok**, ne čas: čas konce
        // se volí až uvnitř uzávěrky, kde k němu patří (předvyplněný plán +
        // „V jiný čas"). Dřív ho nesla už tahle kaskáda a „Uzavřít v 22:00"
        // se četlo jako ukončení směny — přitom auto je zrovna vrácené a na
        // řadě je pokladna.
        //
        // Aktivní až po odklikání povinných položek checklistu fáze ukončení.
        ctaLabel = checklistDone ? 'Uzavřít pokladnu' : zbyvaLabel;
        ctaIcon = <Receipt size={16} />;
        ctaOnClick = onUzaverka;
        ctaCanClick = checklistDone;
    } else if (ukonceniNeedsKorekce) {
        // Uzávěrka hlavní pokladny manažera — stejná úvaha jako u rozvozce:
        // krok, ne čas. Manažer si čas konce zvolí až na finálním „Odhlásit",
        // kde se mu mzda počítá. Aktivní až po checklistu.
        ctaLabel = checklistDone ? 'Uzavřít pokladnu' : zbyvaLabel;
        ctaIcon = <CreditCard size={16} />;
        ctaOnClick = onKorekce;
        ctaCanClick = checklistDone;
    } else if (cekaNaManazera) {
        // Tlačítko nese jméno překážky, ne akci — kliknout nejde a nemá to
        // budit dojem, že by mělo. Vyřešit to musí manažer, ne tenhle displej.
        ctaLabel = 'Čeká na potvrzení manažera';
        ctaIcon = <Hourglass size={16} />;
        ctaOnClick = null;
        ctaCanClick = false;
    } else if (isEnd) {
        const ctaTime = (konecPevny && odeslanyKonec) || endTimeOverride || fmtTime(smena?.cas_do);
        ctaLabel = checklistDone ? `Odhlásit v ${ctaTime}` : zbyvaLabel;
        ctaIcon = <Square size={16} fill="currentColor" />;
        ctaOnClick = onCta;
        ctaCanClick = canCta;
    } else {
        ctaLabel = checklistDone ? 'Ukončit směnu' : zbyvaLabel;
        ctaIcon = <Square size={16} fill="currentColor" />;
        ctaOnClick = onCta;
        ctaCanClick = canCta;
    }

    const ctaBusyLabel = ukonceniNeedsVratitAuto ? 'Uvolňuji…'
        : (ukonceniNeedsUzaverka || ukonceniNeedsKorekce) ? 'Otevírám…'
        : (isEnd ? 'Odhlašuji…' : 'Ukončuji…');

    // Dělené tlačítko „Jiný čas" jen u finálního odhlášení — tam se čas
    // opravdu zadává a rovnou z něj plyne mzda.
    //
    // Nenabízí se u uvolnění vozidla ani u uzávěrky: obě jsou kroky, ne
    // okamžiky, a uzávěrka má vlastní volbu času uvnitř („Uzavřít v 22:00"
    // / „V jiný čas"), takže dvě různá místa pro totéž by si konkurovala.
    // A ne při čekání na manažera — není co časovat, dokud potvrzení nedorazí.
    const ctaSecondary = (isEnd && !ukonceniNeedsVratitAuto && !ukonceniNeedsUzaverka
        && !ukonceniNeedsKorekce && !cekaNaManazera && !konecPevny && onCtaEditTime)
        ? { icon: <Pencil size={13} />, label: 'Jiný čas', onClick: onCtaEditTime, title: 'Zvolit vlastní čas konce směny' }
        : null;

    // Zpět do průběhu jen dokud se v ukončení nestalo nic nevratného —
    // auto pořád převzaté (nebo žádné), pokladna otevřená, konec neodeslaný.
    // Server to hlídá znovu (MojeSmenController::vratitDoPrubehu).
    const lzeVratit = isEnd && !!onVratitDoPrubehu && !smena?.vozidlo_vraceno_at
        && !pokladnaUzavrena && !cekaNaManazera && !dochazka?.navrzeny_clock_out;

    const zbyva = useZbyva(smena, endTimeOverride);
    const { pomer, minutDoKonce } = usePrubehSmeny(smena, endTimeOverride);

    if (!smena) return null;

    // Cockpit 2.0 (2026-10-19): v průběhu na mobilu kompaktní panel —
    // „Ukončit směnu" se ťuká jednou za den, tak je v nabídce ⋯. Tlačítko se
    // vrátí do panelu samo v posledních 30 minutách, kdy je na řadě.
    const kompaktni = isMobile && !isEnd;
    const blizkoKonce = minutDoKonce != null && minutDoKonce <= 30;
    const fazeTed = (smena.faze || []).find(x => x.id === aktivniFazeId) ?? smena.faze?.[0];

    // Dlaždice auto a pokladna; kolegové jsou třetí a přidává je `StalyPruh`
    // sám, aby byly ve všech fázích stejné.
    // Řidič, který auto za běhu vrátil (výměna, porucha), si musí mít kde
    // vzít další — jinak zůstal bez auta až do konce směny (2026-10-19).
    const muzePrevzit = !isEnd && !maAuto && !!smena?.pozaduje_vozidlo && !!onPrevzitAuto;
    const sloty = [
        {
            id: 'auto',
            variant: ukonceniLocked ? 'readonly' : (maAuto ? 'active' : muzePrevzit ? 'action' : 'info'),
            disabled: !ukonceniLocked && !maAuto && !muzePrevzit,
            icon: <Truck size={14} />,
            label: 'Auto',
            value: muzePrevzit ? 'Převzít' : (vozidlo?.spz || null),
            onClick: ukonceniLocked ? null : (maAuto ? onAuto : muzePrevzit ? onPrevzitAuto : null),
            vybrano: plocha === 'ridic',
            accent: '#3B82F6',
            live: maAuto && !isEnd,
        },
        {
            id: 'pokladna',
            // Při běžící uzávěrce dlaždice neukazuje zůstatek, ale co se
            // s pokladnou právě děje. Částka vedle stránky plné jiných
            // částek jen mate — a hlavně už neplatí, jakmile se uzávěrka
            // odešle. Barva je fialová jako celá fáze ukončení.
            // Čekání je `active` bez `onClick`: barevný, ale nekliknutelný.
            // Jako `readonly` zešedl a splynul s vyřízeným autem, přestože je
            // to jediná věc na obrazovce, která se ještě hýbe.
            // Bez pokladny dlaždice jen říká „Ne" — uzávěrka ani čekání se
            // jí netýkají, i když zrovna běží závěrečná obrazovka.
            variant: !maPokladnu ? 'info'
                : (uzaverkaBezi || cekaNaManazera) ? 'active'
                : ukonceniLocked ? 'readonly'
                : ((ukonceniNeedsUzaverka || ukonceniNeedsKorekce) ? 'action' : 'active'),
            disabled: !maPokladnu,
            icon: <Banknote size={14} />,
            label: 'Pokladna',
            // Do dlaždice se vejde jedno krátké slovo — „Ke schválení" se
            // ořízlo na „Ke schvá…". Co se čeká, říká tlačítko v hraně pruhu.
            value: !maPokladnu ? null
                : uzaverkaBezi ? 'Uzávěrka'
                : cekaNaManazera ? 'Čeká'
                : fmtKc(pokladnaSmena.aktualni_kc),
            // Menu pokladny (vklad / výdej / nákup) se při uzávěrce nehodí —
            // pokladna se právě zavírá, ne obsluhuje.
            onClick: (uzaverkaBezi || ukonceniLocked) ? null : (maPokladnu ? onPokladna : null),
            vybrano: plocha === 'pokladna' && !uzaverkaBezi,
            accent: (maPokladnu && uzaverkaBezi) ? '#975ACD'
                : (maPokladnu && cekaNaManazera) ? '#F59E0B' : '#10B981',
            live: maPokladnu && (uzaverkaBezi || cekaNaManazera || !isEnd),
        },
    ];

    return (
        // Ne `flexShrink: 0` na obalu: obal je celá obrazovka modulu a musí
        // vyplnit výšku, aby pracovní plocha uvnitř měla vůči čemu růst.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
            {navigaceOtevrena && <NastaveniNavigace open onClose={() => setNavigaceOtevrena(false)} isDark={isDark} />}
            {/* Název tu nestojí — uvolněný řádek dostává pracovní plocha.
                Kterou směnu člověk dělá, říká rozšířený detail před zahájením;
                uprostřed práce je to informace, kterou už zná, a časy nese osa
                fází (Start → odpočet → Konec). */}
            <StalyPruh
                barvaFaze={phaseColor}
                faze={smena.faze || []}
                aktivniFazeId={aktivniFazeId}
                onOtevritFazi={onOtevritFazi}
                stred={zbyva}
                sloty={sloty}
                tym={tym}
                onTym={onOpenTeam}
                tymVybrano={plocha === 'kolegove'}
                isDark={isDark}
                isMobile={isMobile}
                kompaktni={kompaktni}
                pomerCasu={pomer}
                nabidka={kompaktni ? [
                    { label: 'Ukončit směnu', onClick: ctaOnClick, disabled: !ctaCanClick || busy, popis: !ctaCanClick ? `nejdřív kontroly · ${zbyvaLabel.toLowerCase()}` : null },
                    ...(onOtevritFazi && fazeTed ? [{ label: 'Fáze směny', onClick: () => onOtevritFazi(fazeTed) }] : []),
                    // Rozvozce s autem: hlas a upozornění navigace pro sebe (2026-10-26).
                    ...(maAuto ? [{ label: 'Nastavení navigace', onClick: () => setNavigaceOtevrena(true), popis: 'hlas a upozornění' }] : []),
                ] : []}
                /* Jedna akce, která posune směnu dál — spodní hrana pruhu
                   v barvě fáze. Při běžící uzávěrce hrana zůstane jen
                   linkou: uzávěrka si nese vlastní tlačítko. */
                akce={(uzaverkaBezi || (kompaktni && !blizkoKonce)) ? null : (<>
                    <ShiftCardCta
                        label={ctaLabel}
                        icon={ctaIcon}
                        onClick={ctaOnClick}
                        disabled={!ctaCanClick}
                        busy={busy}
                        busyLabel={ctaBusyLabel}
                        barva={phaseColor}
                        // Během průběhu je ukončení tiché — hlavní akce je
                        // v práci pod panelem (návrh „Horní panel směny").
                        tichy={!isEnd && !ukonceniNeedsVratitAuto && !ukonceniNeedsUzaverka && !ukonceniNeedsKorekce && !cekaNaManazera}
                        secondary={ctaSecondary}
                        isDark={isDark}
                    />
                    {lzeVratit && (
                        <button type="button" onClick={onVratitDoPrubehu} disabled={busy}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                minHeight: 36, marginTop: -4, border: 'none', background: 'none', cursor: 'pointer',
                                font: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)',
                            }}>
                            <Undo2 size={14} /> Zpět do průběhu směny
                        </button>
                    )}
                </>)}
            />

            {/* Chyba patří k tlačítku, které ji vyvolalo — to je teď hrana
                pruhu, takže stojí hned pod ní, ne pod pracovní plochou. */}
            {error && (
                <div style={{
                    margin: isMobile ? '0 16px' : 0,
                    padding: '8px 12px', borderRadius: 7, fontSize: 12,
                    background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                    border: '1px solid rgba(239,68,68,0.3)',
                }}>{error}</div>
            )}

            {children}
        </div>
    );
}
